<template>
  <li class="voicemail-item" v-if="!deleted">
    <div class="unread-status">
      <status-indicator v-if="!markedRead" status="active"> </status-indicator>
    </div>
    <div class="voicemail-source" @click="toggleActions">
      <div class="call-number">
        {{ callerIdName }}
      </div>
      <div class="call-name">
        {{ numberFormatter(callerIdNum) }}
      </div>
    </div>
    <div class="call-info">
      <div class="call-time">
        {{ monthDaySecondsUnixFormatter(time) }}
      </div>
      <div class="call-duration">
        {{ durationFormatter(duration) }}
      </div>
    </div>
    <div class="voicemail-actions">
      <el-button
        class="table-action-button"
        @click="streamVoicemail"
        v-if="!playActive"
        icon="el-icon-caret-right"
      >
      </el-button>
      <el-button
        class="table-action-button"
        v-loading="fileLoading"
        @click="pauseVoicemail"
        v-if="playActive"
        icon="mdi mdi-pause"
      >
      </el-button>
      <el-tooltip
        class="item"
        effect="dark"
        content="Save voicemail"
        placement="top"
      >
        <el-button
          class="table-action-button"
          @click="saveVoicemail"
          icon="el-icon-document"
        >
        </el-button>
      </el-tooltip>
      <el-button
        class="table-action-button"
        @click="deleteVoicemail"
        icon="el-icon-delete"
      >
      </el-button>
    </div>
    <div class="voicemail-av" v-show="audioURL.length && isStreaming">
      <div :id="'vm-audio-' + id">
        <audio src=""></audio>
      </div>
      <div class="vm-audio-progress">
        <progress :id="'progress-bar-' + id" value="0" max="1"></progress>
        <div class="black-text time-left-text">
          {{ timeLeft }}
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import moment from "moment-timezone";
import feathersClient, {apiV2Host} from "@/feathers/feathers-client";
import { mapGetters } from "vuex";
import { phoneNumberFormatter } from "@/globals/globals";

export default {
  name: "Voicemail",
  props: {
    id: { required: true },
    time: { required: true },
    duration: { required: true },
    callerIdName: { required: true },
    callerIdNum: { required: true },
    status: { required: true },
    dir: { required: true },
  },
  computed: {
    ...mapGetters("voicemails", { getVoicemailBox: "get" }),
    user() {
      return this.$store.state.auth.user;
    },
  },
  data() {
    return {
      playActive: false,
      markedRead: this.status !== "unread",
      deleted: false,
      isStreaming: false,
      isPaused: false,
      audioURL: "",
      audioObject: {},
      fileLoading: false,
      fileRetrieved: false,
      timeLeft: null,
      actionsVisible: false,
    };
  },
  methods: {
    durationFormatter(time) {
      const hours = Math.floor(time / 3600);
      const remainderSeconds = time % 3600;
      const minutes = Math.floor(remainderSeconds / 60);
      const seconds = Math.round(remainderSeconds % 60);

      if (hours > 0) return hours + "hr " + minutes + "m";
      if (minutes > 0) return minutes + "m";
      if (seconds > 0) return seconds + "s";
      return "0";
    },
    monthDaySecondsUnixFormatter(time) {
      const { timezone } = this.user;

      //unix from server. need to use UTC offset instead.
      const unixTime = moment.unix(time).utc();
      const today = moment.tz(timezone);
      const offset = today.utcOffset();
      const minuteOffset = Math.abs(today.utcOffset());

      return offset < 0
        ? moment(unixTime)
            .subtract(minuteOffset, "minutes")
            .format("MM/DD/YY h:mm A")
        : moment(unixTime)
            .add(minuteOffset, "minutes")
            .format("MMM D, YYYY h:mm A");
    },
    timeLeftFormatter(time) {
      const remainderSeconds = time % 3600;
      let minutes = Math.floor(remainderSeconds / 60);
      let seconds = Math.round(remainderSeconds % 60);

      if (minutes > 0) {
        if (minutes < 10) {
          minutes = "0" + minutes;
        }

        if (seconds < 10) {
          seconds = "0" + seconds;
        }

        return minutes + ":" + seconds;
      }

      if (seconds > 0) {
        if (seconds < 10) {
          seconds = "0" + seconds;
        }

        return "00:" + seconds;
      }

      return "00:00";
    },
    dateFormatter(time) {
      const { timezone } = this.user;

      //unix from server. need to use UTC offset instead.
      const unixTime = moment.unix(time).utc();
      const today = moment.tz(timezone);
      const offset = today.utcOffset();
      const minuteOffset = Math.abs(today.utcOffset());
      const dayToday = today.format("YYYY-MM-DD");

      //day message occurred using server timestamp and user offset
      const dayCall =
        offset < 0
          ? moment(unixTime)
              .subtract(minuteOffset, "minutes")
              .format("YYYY-MM-DD")
          : moment(unixTime).add(minuteOffset, "minutes").format("YYYY-MM-DD");

      const dayDifference = moment
        .duration(moment(dayToday).diff(moment(dayCall)))
        .asDays();

      //message happened today
      if (dayDifference === 0) {
        return offset < 0
          ? moment(unixTime).subtract(minuteOffset, "minutes").format("h:mm A")
          : moment(unixTime).add(minuteOffset, "minutes").format("h:mm A");
      }

      //message happened yesterday
      if (dayDifference === 1) return "Yesterday";

      //message happened in the recent week
      if (dayDifference >= 2 && dayDifference <= 6) {
        return offset < 0
          ? moment(unixTime).subtract(minuteOffset, "minutes").format("dddd")
          : moment(unixTime).add(minuteOffset, "minutes").format("dddd");
      }

      //message is older than a week
      return offset < 0
        ? moment(unixTime).subtract(minuteOffset, "minutes").format("M/D")
        : moment(unixTime).add(minuteOffset, "minutes").format("M/D");
    },
    numberFormatter(number) {
      return number ? phoneNumberFormatter(number) : number;
    },
    async streamVoicemail() {
      this.playActive = true;
      if (!this.isStreaming && !this.fileRetrieved) {
        this.fileLoading = true;
        try {
          const response = await fetch(`${apiV2Host}/voicemail/messages/${this.id}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          });

          if (response.status !== 200) {
            this.$notify({
              title: "Not Found",
              message: "File Not Found.",
              type: "warning",
            });
            this.fileLoading = false;
            return;
          }

          const results = await response.json();
          if (!results.mediaURL) {
            this.$notify({
              title: "Not Found",
              message: "File Not Found.",
              type: "warning",
            });
          }

          this.audioURL = results.mediaURL;
          this.fileLoading = false;
          this.audioObject = document
            .getElementById("vm-audio-" + this.id)
            .querySelector("audio");

          this.audioObject.src = this.audioURL;

          this.isStreaming = true;
          this.fileRetrieved = true;

          this.audioObject.addEventListener("ended", () => {
            this.playActive = false;
            this.isStreaming = false;
            this.isPaused = false;
          });

          this.audioObject.addEventListener("timeupdate", () => {
            this.initProgressBar();
          });

          this.audioObject.play().catch(error => {
            console.error("Error playing audio:", error);
          });
        } catch (error) {
          console.log(error);
          this.$notify({
            title: "Not Found",
            message: "File Not Found.",
            type: "warning",
          });
          this.fileLoading = false;
          return;
        }
      }

      if (this.isStreaming) {
        this.audioObject.play().catch(error => {
          console.error("Error playing audio:", error);
          this.playActive = false;
        });
      }

      if (!this.isStreaming && this.fileRetrieved) {
        this.audioObject.play().catch(error => {
          console.error("Error playing audio:", error);
          this.playActive = false;
        }).then(() => {
          this.playActive = true;
          this.isStreaming = true;
        });
      }

      if (!this.markedRead && this.status === "unread") {
        await this.updateVoicemail();
      }
    },
    initProgressBar() {
      const length = this.audioObject.duration ? this.audioObject.duration : 0;
      const current_time = this.audioObject.currentTime;
      if (length) {
        this.timeLeft = this.timeLeftFormatter(length - current_time);
      }

      let progressBar = document.getElementById("progress-bar-" + this.id);
      progressBar.value = length ? current_time / length : length;
      progressBar.addEventListener("click", (event) => {
        const percent = event.offsetX / progressBar.offsetWidth;
        this.audioObject.currentTime = percent * length;
        progressBar.value = percent / 100;
      });
    },
    pauseVoicemail() {
      this.playActive = false;
      if (this.isStreaming) {
        this.audioObject.pause();
        this.isPaused = true;
      }
    },
    async updateVoicemail() {
      try {
        await fetch(`${apiV2Host}/voicemail/messages/mark-read/${this.id}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        });

        this.markedRead = true;
      } catch (e) {
        console.log(e);
        this.markedRead = false;
        this.$notify({
          title: "Error",
          message: "Could not mark as read.",
          type: "error",
        });
      }
    },
    async saveVoicemail() {
      try {
        const res = await fetch(`${apiV2Host}/voicemail/messages/${this.id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        });

        const results = await res.json();

        if (!results.mediaURL) {
          this.$notify({
            title: "Not Found",
            message: "Could not save file.",
            type: "error",
          });
        } else {
          const link = document.createElement("a");
          link.href = results.mediaURL;
          link.download = `${this.id}.wav`;
          link.click();
        }
      } catch (e) {
        this.$notify({
          title: "Not Found",
          message: "Could not save file.",
          type: "error",
        });
      }
    },
    deleteVoicemail() {
      this.$confirm("Delete Voicemail?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      })
        .then(async () => {
          await fetch(`${apiV2Host}/voicemail/messages/${this.id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          })
            .then(() => {
              this.deleted = true;
            });
        })
        .catch(() => true);
    },
    toggleActions() {
      this.actionsVisible = !this.actionsVisible;
    },
  },
  mounted() {
    if (this.status === "unread" && !this.markedRead) {
      this.$store.commit("addVoicemailCount");
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.unread-status {
  min-width: 20px;
}

.call-number,
.call-name {
  margin-left: 10px;
}

.call-name {
  font-size: 10pt;
}

.call-duration {
  font-size: 10pt;
}

.call-info {
  margin: 0 0 0 30px;
  min-width: 200px;
}

.vm-audio {
  canvas {
    display: none !important;
  }
}

.voicemail-source {
  width: 200px;
  max-width: 200px;
  cursor: pointer;
}

.vm-audio-progress {
  margin-left: 20px;
  display: flex;
  align-items: center;
}

.time-left-text {
  margin-left: 10px;
}

progress[value] {
  /* Reset the default appearance */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 150px;
  height: 10px;
}

progress[value]::-webkit-progress-bar {
  background-color: #eee;
  border-radius: 50px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25) inset;
}

progress[value]::-moz-progress-bar {
  background-color: #eee;
  border-radius: 50px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25) inset;
}

progress[value]::-webkit-progress-value {
  background: transparentize($voxo-blue, 0.2);
  border-radius: 50px;
}

@media (max-width: 959px) {
  progress[value] {
    width: 200px;
    height: 10px;
  }

  .voicemail-actions,
  .voicemail-av {
    display: flex;
    flex-basis: 100%;
    margin-top: 15px;
    margin-left: 25px;
  }

  .vm-audio-progress {
    margin-left: 0;
  }

  .voicemail-item {
    flex-wrap: wrap;
  }

  .voicemail-source {
    width: 150px;
  }

  .call-info {
    margin-right: 5px;
  }
}
</style>
